import { formatCount, formatNaira, formatPercent } from '../../../../utils/money';
import { formatUtc } from '../../../../utils/datetime';
import { Card, DetailRow, Skeleton, StatusPill } from '../../../ui';

const muted = 'text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor';

const Pair = ({ children }) => (
  <div className="grid grid-cols-12 divide-theme-border py-6 first:pt-0 last:pb-0 max-md:divide-y md:divide-x dark:divide-themedark-border">
    {children}
  </div>
);

const Block = ({ title, scope, note, children }) => (
  <section className="col-span-12 flex flex-col max-md:pt-6 max-md:first:pt-0 md:col-span-6 md:first:pr-6 md:last:pl-6">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-theme-border pb-2 dark:border-themedark-border">
      <h6 className="mb-0 text-f-h6 font-semibold text-theme-headings dark:text-themedark-headings">{title}</h6>
      <span className={`text-xs font-medium uppercase tracking-wide ${muted}`}>{scope}</span>
    </div>
    {children}
    {note && <p className={`mt-auto mb-0 pt-4 text-sm ${muted}`}>{note}</p>}
  </section>
);

const LoadingBlock = () => (
  <section className="col-span-12 max-md:pt-6 max-md:first:pt-0 md:col-span-6 md:first:pr-6 md:last:pl-6">
    <Skeleton w="45%" h="0.9rem" />
    <Skeleton className="mt-5" h="0.85rem" />
    <Skeleton className="mt-4" h="0.85rem" />
    <Skeleton className="mt-4" h="0.85rem" />
  </section>
);

const LoadingRows = () => (
  <div className="divide-y divide-theme-border dark:divide-themedark-border">
    <Pair>
      <LoadingBlock />
      <LoadingBlock />
    </Pair>
    <Pair>
      <LoadingBlock />
      <LoadingBlock />
    </Pair>
  </div>
);

const OperationsPanel = ({ operations, credential, loading }) => {
  const pansByStatus = Object.entries(operations?.pans_by_status ?? {});
  const webhooks = operations?.webhooks;
  const settlement = operations?.settlement;

  const credentialClassName = credential?.expired
    ? 'text-danger-700'
    : credential?.needsAttention
      ? 'text-warning-700'
      : '';

  return (
    <Card title="Operations" className="mb-0 h-full">
      {loading ? (
        <LoadingRows />
      ) : (
        <div className="divide-y divide-theme-border dark:divide-themedark-border">
          <Pair>
            <Block title="PANs by status" scope="Current state">
              {pansByStatus.length === 0 ? (
                <p className={`mb-0 pt-3 text-sm ${muted}`}>No PANs issued yet.</p>
              ) : (
                pansByStatus.map(([status, count]) => (
                  <DetailRow key={status} label={<StatusPill status={status} />} value={formatCount(count)} />
                ))
              )}
            </Block>

            <Block title="API credentials" scope="Current state">
              {credential ? (
                <>
                  <DetailRow
                    label="Status"
                    value={<span className={credentialClassName}>{credential.label}</span>}
                  />
                  <DetailRow label="Expires" value={formatUtc(credential.expiresAt)} />
                </>
              ) : (
                <p className={`mb-0 pt-3 text-sm ${muted}`}>No credential expiry on record.</p>
              )}
            </Block>
          </Pair>

          <Pair>
            <Block
              title="Webhooks"
              scope="Selected period"
              note="The response rate counts attempts that came back with a response of any kind."
            >
              <DetailRow label="Delivered" value={formatCount(webhooks?.delivered_count)} />
              <DetailRow label="Failed" value={formatCount(webhooks?.failed_count)} />
              <DetailRow
                label="Response rate"
                value={
                  webhooks?.success_rate === null || webhooks?.success_rate === undefined
                    ? 'No attempts yet'
                    : formatPercent(webhooks.success_rate)
                }
              />
            </Block>

            <Block
              title="Settlement"
              scope="Selected period"
              note="Three separate figures, not a breakdown of one total."
            >
              <DetailRow label="Settled" value={formatNaira(settlement?.success_amount)} />
              <DetailRow label="Pending" value={formatNaira(settlement?.pending_amount)} />
              <DetailRow label="Failed" value={formatCount(settlement?.failed_count)} />
            </Block>
          </Pair>
        </div>
      )}
    </Card>
  );
};

export default OperationsPanel;
